import { Component, OnInit, OnDestroy, Output, EventEmitter, ElementRef, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Subscription } from 'rxjs';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RefreshService } from '../../services/refresh.service';

@Component({
  selector: 'app-pull-to-refresh',
  templateUrl: './pull-to-refresh.component.html',
  styleUrls: ['./pull-to-refresh.component.css'],
  standalone: true,
  imports: [ProgressSpinnerModule]
})
export class PullToRefreshComponent implements OnInit, OnDestroy {
  private startY: number = 0;
  private touchMoveListener: any;
  private touchStartListener: any;
  private touchEndListener: any;

  public refreshing: boolean = false;
  public pullDistance: number = 0;
  private pullThreshold: number = 80; // Distance required to trigger refresh

  @Output() refresh = new EventEmitter<void>();

  constructor(
    private elementRef: ElementRef,
    private refreshService: RefreshService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupPullToRefresh();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.removePullToRefresh();
    }
  }

  private setupPullToRefresh(): void {
    // Only add event listeners if we're in a browser environment
    const element = document.body;

    this.touchStartListener = (e: TouchEvent) => this.onTouchStart(e);
    this.touchMoveListener = (e: TouchEvent) => this.onTouchMove(e);
    this.touchEndListener = (e: TouchEvent) => this.onTouchEnd(e);

    element.addEventListener('touchstart', this.touchStartListener, { passive: true });
    element.addEventListener('touchmove', this.touchMoveListener, { passive: false });
    element.addEventListener('touchend', this.touchEndListener, { passive: true });
  }

  private removePullToRefresh(): void {
    const element = document.body;

    if (this.touchStartListener) {
      element.removeEventListener('touchstart', this.touchStartListener);
    }

    if (this.touchMoveListener) {
      element.removeEventListener('touchmove', this.touchMoveListener);
    }

    if (this.touchEndListener) {
      element.removeEventListener('touchend', this.touchEndListener);
    }
  }

  private onTouchStart(event: TouchEvent): void {
    // Store the initial touch position, but only if at the top of the page
    if (window.scrollY <= 10) {
      this.startY = event.touches[0].clientY;
    } else {
      this.startY = 0;
    }
  }

  private onTouchMove(event: TouchEvent): void {
    if (this.startY === 0 || this.refreshing) return;

    // Calculate the pull distance
    const touchY = event.touches[0].clientY;
    const distance = touchY - this.startY;

    // Only activate when pulling down at the top of the page
    if (distance > 0 && window.scrollY <= 0) {
      // Calculate a resistance factor - the further you pull, the harder it gets
      this.pullDistance = Math.min(distance * 0.5, this.pullThreshold * 1.5);

      // Prevent the default scroll behavior when showing the pull indicator
      if (this.pullDistance > 5) {
        event.preventDefault();
      }
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    if (this.pullDistance >= this.pullThreshold && !this.refreshing) {
      this.executeRefresh();
    }

    // Reset the pull distance when touch ends
    this.pullDistance = 0;
  }

  private executeRefresh(): void {
    this.refreshing = true;
    this.refresh.emit();

    // Trigger the refresh
    this.refreshService.refreshCurrentRoute();

    // Hide the refresh indicator after a short delay
    setTimeout(() => {
      this.refreshing = false;
    }, 1500);
  }
}
